import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  ChatApprovalContract,
  ChatContract,
  ChatDraftContract,
  ConfidenceLevel,
  CopilotConversation,
  CopilotMessage,
  EvidenceCitation,
  RoleScope,
} from '@lineageatlas/shared';
import { isFeatureEnabled } from '../../config/featureFlags';
import { productConfig } from '../../config/product';
import {
  archiveCopilotConversation,
  createCopilotApproval,
  getCopilotConversation,
  isFeatureDisabledError,
  listCopilotConversations,
  restoreCopilotConversation,
  sendCopilotChat,
  type CopilotConversationDetail,
} from '../../lib/api/regenloop';

type TranscriptItem =
  | {
      kind: 'notice';
      id: string;
      content: string;
    }
  | {
      kind: 'message';
      id: string;
      role: CopilotMessage['role'];
      content: string;
      createdAt?: string;
      contract?: ChatContract | null;
    };

type ApprovalStatus = 'approved' | 'rejected' | 'superseded';

type ApprovalRecord = {
  state: ApprovalStatus;
  at: string;
  reason?: string | null;
};

const DISABLED_NOTICE: TranscriptItem = {
  kind: 'notice',
  id: 'disabled-system-boundary',
  content:
    'Co-pilot chat is disabled until VITE_LINEAGE_ENABLE_COPILOT_CHAT=true is set and the approved backend route is available.',
};

function SparkIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2l1.7 6.1L20 10l-6.3 1.9L12 18l-1.7-6.1L4 10l6.3-1.9L12 2z" />
      <path d="M19 15l.8 2.8L22 19l-2.2.7L19 22l-.8-2.3L16 19l2.2-1.2L19 15z" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3l7 3v5c0 4.4-2.8 8.2-7 10-4.2-1.8-7-5.6-7-10V6l7-3z" />
      <path d="M9 12l2 2 4-5" />
    </svg>
  );
}

function ArchiveIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 7h16" />
      <path d="M6 7v12h12V7" />
      <path d="M9 11h6" />
      <path d="M5 4h14v3H5z" />
    </svg>
  );
}

function RestoreIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <path d="M3 4v5h5" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 13l4 4L19 7" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 6l12 12" />
      <path d="M18 6L6 18" />
    </svg>
  );
}

function parseContract(message: CopilotMessage): ChatContract | null {
  if (!message.contract_json) return null;
  try {
    return JSON.parse(message.contract_json) as ChatContract;
  } catch {
    return null;
  }
}

function messageToTranscript(message: CopilotMessage): TranscriptItem {
  return {
    kind: 'message',
    id: message.id,
    role: message.role,
    content: message.content,
    createdAt: message.created_at,
    contract: parseContract(message),
  };
}

function contractToTranscript(contract: ChatContract): TranscriptItem {
  return {
    kind: 'message',
    id: contract.message_id,
    role: 'assistant',
    content: contractContent(contract),
    createdAt: contract.generated_at,
    contract,
  };
}

function localUserTranscript(content: string): TranscriptItem {
  return {
    kind: 'message',
    id: `local-${Date.now()}`,
    role: 'user',
    content,
    createdAt: new Date().toISOString(),
  };
}

function contractContent(contract: ChatContract): string {
  if (contract.contract_type === 'chat_answer') return contract.answer;
  if (contract.contract_type === 'chat_refusal') return contract.message;
  if (contract.contract_type === 'chat_draft') return contract.draft_body;
  return `Human ${contract.review_state} AI draft ${contract.draft_message_id}.`;
}

function confidencePercent(confidence: ConfidenceLevel | undefined): number {
  if (confidence === 'high') return 85;
  if (confidence === 'medium') return 60;
  if (confidence === 'low') return 35;
  return 0;
}

function confidenceLabel(confidence: ConfidenceLevel | undefined): string {
  return confidence ? `${confidence} confidence` : 'Confidence pending';
}

function citationTitle(citation: EvidenceCitation): string {
  return citation.title ?? citation.label ?? citation.source_type;
}

function citationDetail(citation: EvidenceCitation): string {
  const parts = [
    citation.source_type,
    citation.source_id,
    citation.section,
    citation.grounding ? `grounding: ${citation.grounding}` : null,
  ].filter(Boolean);
  return parts.join(' | ');
}

function formatDate(value?: string | null): string {
  if (!value) return 'No timestamp';
  return new Date(value).toLocaleString();
}

function formatList(value: string[] | 'all_accessible' | 'none'): string {
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'none';
  return value.replace('_', ' ');
}

function roleScopeSummary(roleScope?: RoleScope): string {
  if (!roleScope) return 'Role scope pending';
  return `${roleScope.role} | lots: ${formatList(roleScope.data_scope.lot_ids)} | patients: ${formatList(
    roleScope.data_scope.patient_codes,
  )}`;
}

function hasApprovalCapability(roleScope?: RoleScope): boolean {
  return roleScope?.capabilities.includes('approve_ai_draft') === true;
}

function isArchived(conversation: CopilotConversation): boolean {
  return conversation.archived_at != null;
}

function featureErrorMessage(error: unknown): string {
  if (isFeatureDisabledError(error)) {
    return 'Co-pilot chat is disabled by the backend feature gate.';
  }
  return error instanceof Error ? error.message : 'Co-pilot endpoint unavailable';
}

function ApprovalMessage({ contract }: { contract: ChatApprovalContract }) {
  return (
    <div className="rl-act done">
      <div className="rl-act-resolved">
        <span className="ic">
          <CheckIcon />
        </span>
        <div>
          <div className="t">Human {contract.review_state} AI draft</div>
          <div className="s">
            {contract.approver_role} | {formatDate(contract.generated_at)}
          </div>
        </div>
      </div>
    </div>
  );
}

function DraftApprovalCard({
  contract,
  approval,
  approvalBusy,
  onDecision,
}: {
  contract: ChatDraftContract;
  approval?: ApprovalRecord;
  approvalBusy: boolean;
  onDecision: (contract: ChatDraftContract, state: ApprovalStatus) => void;
}) {
  const canApprove = hasApprovalCapability(contract.role_scope);
  const approverCopy = contract.required_approver_roles.join(', ');

  if (approval) {
    return (
      <div className="rl-act done">
        <div className="rl-act-resolved">
          <span className="ic">
            <CheckIcon />
          </span>
          <div>
            <div className="t">Draft review recorded: {approval.state}</div>
            <div className="s">{formatDate(approval.at)}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rl-act routed">
      <div className="rl-act-h">
        <span className="ai">
          <SparkIcon />
        </span>
        <div>
          <div className="kind">Draft requires human confirmation</div>
          <div className="ttl">
            {contract.title} <span className="mono">| {contract.draft_type}</span>
          </div>
        </div>
      </div>
      <div className="rl-act-prev">{contract.draft_body}</div>
      <div className="rl-act-foot">
        <span className="note">
          <b>Guardrail:</b> approval records the human decision only. It does not mutate records,
          release lots, provide medical advice, or turn a signal into an efficacy claim.
          {approverCopy ? ` Required roles: ${approverCopy}.` : ''}
        </span>
        <button
          className="rl-btn gho"
          type="button"
          disabled={approvalBusy}
          onClick={() => onDecision(contract, 'rejected')}
        >
          <XIcon /> Reject
        </button>
        <button
          className="rl-btn gho"
          type="button"
          disabled={approvalBusy}
          onClick={() => onDecision(contract, 'superseded')}
        >
          Supersede
        </button>
        <button
          className="rl-btn pri"
          type="button"
          disabled={approvalBusy || !canApprove}
          title={canApprove ? 'Record human approval' : 'Current role cannot approve AI drafts'}
          onClick={() => onDecision(contract, 'approved')}
        >
          <CheckIcon /> Record approval
        </button>
      </div>
    </div>
  );
}

function ContractMeta({ contract }: { contract: ChatContract }) {
  const confidence =
    contract.contract_type === 'chat_answer' || contract.contract_type === 'chat_draft'
      ? contract.confidence
      : undefined;
  const pct = confidencePercent(confidence);
  const grounding =
    contract.contract_type === 'chat_answer' || contract.contract_type === 'chat_draft'
      ? contract.grounding
      : contract.citations[0]?.grounding;

  return (
    <div className="m-meta">
      <span className="rl-conf-pill">
        <span className="bar">
          <span style={{ width: `${pct}%` }} />
        </span>
        {confidenceLabel(confidence)}
      </span>
      <span className="rl-chip vio">Grounding | {grounding ?? 'pending'}</span>
      <span className="rl-chip acc">Scope | {roleScopeSummary(contract.role_scope)}</span>
    </div>
  );
}

function CitationChips({ citations }: { citations: EvidenceCitation[] }) {
  if (!citations.length) {
    return (
      <div className="m-chips">
        <span className="rl-ev-chip">No citations returned</span>
      </div>
    );
  }

  return (
    <div className="m-chips">
      {citations.map((citation, index) => (
        <span
          className="rl-ev-chip"
          key={`${citation.source_type}-${citation.source_id ?? index}-${citation.label}`}
          title={citationDetail(citation)}
        >
          {citationTitle(citation)}
        </span>
      ))}
    </div>
  );
}

function Limitations({ contract }: { contract: ChatContract }) {
  const limitations =
    contract.contract_type === 'chat_answer' || contract.contract_type === 'chat_draft'
      ? contract.limitations
      : contract.contract_type === 'chat_refusal' && contract.safe_alternative
        ? [contract.safe_alternative]
        : [];

  if (!limitations.length) return null;

  return (
    <div className="rl-refuse tier2">
      <span className="ic">
        <ShieldIcon />
      </span>
      <div className="rt">
        <b>Limitations</b>
        <br />
        {limitations.join(' ')}
      </div>
    </div>
  );
}

function FollowUps({
  contract,
  onUsePrompt,
}: {
  contract: ChatContract;
  onUsePrompt: (prompt: string) => void;
}) {
  if (contract.contract_type !== 'chat_answer' || !contract.follow_up_questions.length) {
    return null;
  }

  return (
    <div className="m-follow">
      {contract.follow_up_questions.map((question) => (
        <button
          className="rl-follow"
          key={question}
          type="button"
          onClick={() => onUsePrompt(question)}
        >
          <SparkIcon /> {question}
        </button>
      ))}
    </div>
  );
}

function AssistantMessage({
  item,
  approval,
  approvalBusy,
  onDecision,
  onUsePrompt,
}: {
  item: Extract<TranscriptItem, { kind: 'message' }>;
  approval?: ApprovalRecord;
  approvalBusy: boolean;
  onDecision: (contract: ChatDraftContract, state: ApprovalStatus) => void;
  onUsePrompt: (prompt: string) => void;
}) {
  const contract = item.contract;

  if (!contract) {
    return (
      <div className="rl-msg agent">
        <div className="m-av">
          <SparkIcon />
        </div>
        <div className="m-body">
          <div className="m-text">
            <p>{item.content}</p>
          </div>
          <div className="m-meta">
            <span className="rl-chip warn">Structured contract pending</span>
          </div>
        </div>
      </div>
    );
  }

  if (contract.contract_type === 'chat_approval') {
    return (
      <div className="rl-msg agent">
        <div className="m-av">
          <SparkIcon />
        </div>
        <div className="m-body">
          <ApprovalMessage contract={contract} />
        </div>
      </div>
    );
  }

  const isRefusal = contract.contract_type === 'chat_refusal';
  const text = contractContent(contract);

  return (
    <div className="rl-msg agent">
      <div className="m-av">
        <SparkIcon />
      </div>
      <div className="m-body">
        {isRefusal ? (
          <div className="rl-refuse tier2">
            <span className="ic">
              <ShieldIcon />
            </span>
            <div className="rt">
              <b>Request refused: {contract.refusal_reason}</b>
              <br />
              {text}
            </div>
          </div>
        ) : (
          <div className="m-text">
            <p>{text}</p>
          </div>
        )}
        <ContractMeta contract={contract} />
        <CitationChips citations={contract.citations} />
        <Limitations contract={contract} />
        {contract.contract_type === 'chat_draft' && (
          <DraftApprovalCard
            contract={contract}
            approval={approval}
            approvalBusy={approvalBusy}
            onDecision={onDecision}
          />
        )}
        <FollowUps contract={contract} onUsePrompt={onUsePrompt} />
      </div>
    </div>
  );
}

function MessageRow({
  item,
  approval,
  approvalBusy,
  onDecision,
  onUsePrompt,
}: {
  item: TranscriptItem;
  approval?: ApprovalRecord;
  approvalBusy: boolean;
  onDecision: (contract: ChatDraftContract, state: ApprovalStatus) => void;
  onUsePrompt: (prompt: string) => void;
}) {
  if (item.kind === 'notice') {
    return (
      <div className="rl-refuse tier2" style={{ marginTop: 22 }}>
        <div className="ic">
          <ShieldIcon />
        </div>
        <div className="rt">
          <b>Feature boundary</b>
          <br />
          {item.content}
        </div>
      </div>
    );
  }

  if (item.role === 'user') {
    return (
      <div className="rl-msg user">
        <div className="m-av lab">You</div>
        <div className="m-body">
          <div className="m-bubble">{item.content}</div>
        </div>
      </div>
    );
  }

  return (
    <AssistantMessage
      item={item}
      approval={approval}
      approvalBusy={approvalBusy}
      onDecision={onDecision}
      onUsePrompt={onUsePrompt}
    />
  );
}

function ConversationButton({
  conversation,
  selectedId,
  busy,
  onSelect,
  onArchiveToggle,
}: {
  conversation: CopilotConversation;
  selectedId: string | undefined;
  busy: boolean;
  onSelect: (conversation: CopilotConversation) => void;
  onArchiveToggle: (conversation: CopilotConversation, archive: boolean) => void;
}) {
  const archived = isArchived(conversation);

  return (
    <div className={`rl-cd-conv ${selectedId === conversation.id ? 'on' : ''}`}>
      <button className="main" type="button" onClick={() => onSelect(conversation)}>
        <div className="t">{conversation.title}</div>
        <div className="s">
          {archived ? 'archived' : 'active'} | {formatDate(conversation.updated_at)}
        </div>
      </button>
      <button
        className="act"
        type="button"
        disabled={busy}
        title={archived ? 'Restore conversation' : 'Archive conversation'}
        onClick={() => onArchiveToggle(conversation, !archived)}
      >
        {archived ? <RestoreIcon /> : <ArchiveIcon />}
      </button>
    </div>
  );
}

function ConversationDrawer({
  conversations,
  selectedId,
  loading,
  error,
  busyConversationId,
  onNew,
  onSelect,
  onArchiveToggle,
}: {
  conversations: CopilotConversation[];
  selectedId: string | undefined;
  loading: boolean;
  error: string | null;
  busyConversationId: string | null;
  onNew: () => void;
  onSelect: (conversation: CopilotConversation) => void;
  onArchiveToggle: (conversation: CopilotConversation, archive: boolean) => void;
}) {
  const active = conversations.filter((conversation) => !isArchived(conversation));
  const archived = conversations.filter(isArchived);

  return (
    <aside className="rl-cd-drawer rl-panel" aria-label="Conversation archive">
      <div className="rl-panel-h">
        <h3>Conversation archive</h3>
        <span className="mut">Soft archive and restore</span>
      </div>
      <div className="rl-cd-body">
        <button className="rl-btn pri rl-cd-new" type="button" onClick={onNew}>
          New grounded chat
        </button>
        {loading ? (
          <p className="rl-cd-empty">Loading conversations...</p>
        ) : error ? (
          <p className="rl-cd-empty">Conversation endpoint unavailable: {error}</p>
        ) : conversations.length === 0 ? (
          <p className="rl-cd-empty">No saved conversations returned.</p>
        ) : (
          <>
            <div className="rl-cd-sec">Active</div>
            {active.length === 0 && <p className="rl-cd-empty">None returned.</p>}
            {active.map((conversation) => (
              <ConversationButton
                key={conversation.id}
                conversation={conversation}
                selectedId={selectedId}
                busy={busyConversationId === conversation.id}
                onSelect={onSelect}
                onArchiveToggle={onArchiveToggle}
              />
            ))}
            <div className="rl-cd-sec">
              Archived <span className="mut">retained</span>
            </div>
            {archived.length === 0 && <p className="rl-cd-empty">None returned.</p>}
            {archived.map((conversation) => (
              <ConversationButton
                key={conversation.id}
                conversation={conversation}
                selectedId={selectedId}
                busy={busyConversationId === conversation.id}
                onSelect={onSelect}
                onArchiveToggle={onArchiveToggle}
              />
            ))}
          </>
        )}
        <div className="rl-cd-note">
          <ArchiveIcon />
          <span>
            Archiving hides a conversation without deleting it. Restored conversations fetch their
            full server-side detail before review.
          </span>
        </div>
      </div>
    </aside>
  );
}

export function CopilotPage() {
  const enabled = isFeatureEnabled('copilotChat');
  const [conversations, setConversations] = useState<CopilotConversation[]>([]);
  const [conversationDetail, setConversationDetail] = useState<CopilotConversationDetail | null>(
    null,
  );
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [messages, setMessages] = useState<TranscriptItem[]>(enabled ? [] : [DISABLED_NOTICE]);
  const [message, setMessage] = useState('');
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [conversationError, setConversationError] = useState<string | null>(null);
  const [chatError, setChatError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [busyConversationId, setBusyConversationId] = useState<string | null>(null);
  const [approvalBusyId, setApprovalBusyId] = useState<string | null>(null);
  const [approvals, setApprovals] = useState<Record<string, ApprovalRecord>>({});

  const activeConversation = useMemo(
    () => conversations.find((conversation) => conversation.id === conversationId),
    [conversationId, conversations],
  );
  const activeArchived = activeConversation ? isArchived(activeConversation) : false;
  const canSend = enabled && !activeArchived && message.trim() !== '' && !sending;

  const loadConversations = useCallback(async () => {
    if (!enabled) {
      setConversations([]);
      setConversationError(null);
      setMessages([DISABLED_NOTICE]);
      return;
    }

    setConversationsLoading(true);
    setConversationError(null);
    try {
      const response = await listCopilotConversations({ include_archived: true });
      setConversations(response.data);
    } catch (error) {
      setConversationError(featureErrorMessage(error));
    } finally {
      setConversationsLoading(false);
    }
  }, [enabled]);

  const loadConversationDetail = useCallback(
    async (id: string) => {
      if (!enabled) return;
      setDetailLoading(true);
      setChatError(null);
      try {
        const response = await getCopilotConversation(id);
        setConversationDetail(response.data);
        setMessages(response.data.messages.map(messageToTranscript));
      } catch (error) {
        setChatError(featureErrorMessage(error));
      } finally {
        setDetailLoading(false);
      }
    },
    [enabled],
  );

  useEffect(() => {
    void loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    if (!conversationId || !enabled) return;
    void loadConversationDetail(conversationId);
  }, [conversationId, enabled, loadConversationDetail]);

  async function handleSend() {
    const text = message.trim();
    if (!canSend || !text) return;

    setMessages((current) => [...current.filter((item) => item.id !== DISABLED_NOTICE.id), localUserTranscript(text)]);
    setSending(true);
    setChatError(null);
    setStatusMessage(null);
    setMessage('');
    try {
      const response = await sendCopilotChat({
        message: text,
        conversation_id: conversationId,
        intent: /\b(draft|create|generate)\b/i.test(text) ? 'draft' : 'answer',
      });
      setConversationId(response.data.conversation_id);
      setMessages((current) => [...current, contractToTranscript(response.data.contract)]);
      await loadConversations();
    } catch (error) {
      setChatError(featureErrorMessage(error));
    } finally {
      setSending(false);
    }
  }

  function handleSelectConversation(conversation: CopilotConversation) {
    setConversationId(conversation.id);
    setConversationDetail(null);
    setStatusMessage(
      isArchived(conversation)
        ? 'Archived conversation loaded for review. Restore before sending.'
        : 'Conversation detail loading.',
    );
  }

  function handleNewConversation() {
    setConversationId(undefined);
    setConversationDetail(null);
    setMessages([]);
    setApprovals({});
    setChatError(null);
    setStatusMessage('New chat ready. The first message creates a server conversation.');
  }

  async function handleArchiveToggle(conversation: CopilotConversation, archive: boolean) {
    if (!enabled) return;
    setBusyConversationId(conversation.id);
    setStatusMessage(null);
    try {
      const response = archive
        ? await archiveCopilotConversation(conversation.id)
        : await restoreCopilotConversation(conversation.id);
      setConversations((current) =>
        current.map((item) => (item.id === conversation.id ? response.data : item)),
      );
      if (conversationId === conversation.id) {
        setConversationDetail((current) =>
          current ? { ...current, conversation: response.data } : current,
        );
        await loadConversationDetail(conversation.id);
      }
      setStatusMessage(
        archive
          ? 'Conversation archived. It remains restorable from the archive list.'
          : 'Conversation restored. Sending is available again if the backend gate stays on.',
      );
    } catch (error) {
      setStatusMessage(featureErrorMessage(error));
    } finally {
      setBusyConversationId(null);
    }
  }

  async function handleDraftDecision(contract: ChatDraftContract, reviewState: ApprovalStatus) {
    if (!enabled) return;
    setApprovalBusyId(contract.message_id);
    setChatError(null);
    try {
      await createCopilotApproval({
        conversation_id: contract.conversation_id,
        draft_message_id: contract.message_id,
        review_state: reviewState,
        decision_reason:
          reviewState === 'approved'
            ? 'Human confirmed draft content for permitted use.'
            : 'Human did not approve this AI draft.',
      });
      setApprovals((current) => ({
        ...current,
        [contract.message_id]: {
          state: reviewState,
          at: new Date().toISOString(),
        },
      }));
      setStatusMessage(`Human ${reviewState} decision recorded for draft only.`);
      await loadConversationDetail(contract.conversation_id);
    } catch (error) {
      setChatError(featureErrorMessage(error));
    } finally {
      setApprovalBusyId(null);
    }
  }

  const transcript = messages.length > 0 ? messages : [];
  const detailTitle = conversationDetail?.conversation.title ?? activeConversation?.title;

  return (
    <div className="rl-chat">
      <div className="rl-chat-head">
        <button className="rl-chat-role" type="button" disabled={!enabled}>
          <span className="av lab">AI</span>
          <span className="who">
            <span className="n">Co-pilot scope</span>
            <span className="s">
              {activeArchived ? 'Archived read-only' : enabled ? 'Live role-scoped' : 'Disabled'}
            </span>
          </span>
        </button>
        <span className="rl-chat-scope">
          <ShieldIcon /> Scope{' '}
          <b>{enabled ? (activeArchived ? 'read-only archive' : 'live API') : 'frontend off'}</b>
        </span>
        <div className="rl-chat-headtools">
          <button
            className="rl-chat-tool"
            type="button"
            disabled={!enabled || !activeConversation || activeArchived}
            onClick={() =>
              activeConversation && void handleArchiveToggle(activeConversation, true)
            }
          >
            <ArchiveIcon /> Archive
          </button>
          <button
            className="rl-chat-tool"
            type="button"
            disabled={!enabled || !activeConversation || !activeArchived}
            onClick={() =>
              activeConversation && void handleArchiveToggle(activeConversation, false)
            }
          >
            <RestoreIcon /> Restore
          </button>
        </div>
      </div>

      <div className="rl-chat-body">
        <div className="rl-chat-scroll">
          <div className="rl-chat-inner">
            <div className="rl-chat-welcome">
              <div className="mk">
                <SparkIcon />
              </div>
              <h2>
                Chat with your <span className="hl">grounded registry context.</span>
              </h2>
              <p>
                {productConfig.aiDisclaimer} Answers show confidence, grounding, role scope,
                citations, limitations, and any draft remains human-confirmation-only.
              </p>
            </div>
            <div className="rl-chat-sugs">
              <button
                className="rl-sug"
                type="button"
                disabled={!enabled}
                onClick={() =>
                  setMessage(
                    'Summarize candidate signals to investigate for QC review and cite the source records.',
                  )
                }
              >
                <span className="si">
                  <SparkIcon />
                </span>
                <span>
                  <span className="st">Summarize signals</span>
                  <span className="sd">Frame outcome patterns as investigation prompts.</span>
                  <span className="tag">Signals</span>
                </span>
              </button>
              <button
                className="rl-sug"
                type="button"
                disabled={!enabled}
                onClick={() =>
                  setMessage(
                    'Draft a QC review note that requires human confirmation before any action.',
                  )
                }
              >
                <span className="si">
                  <ShieldIcon />
                </span>
                <span>
                  <span className="st">Draft review note</span>
                  <span className="sd">Keep draft and approval boundaries explicit.</span>
                  <span className="tag">Human confirm</span>
                </span>
              </button>
            </div>
            {detailTitle && <p className="rl-cd-empty">Current conversation: {detailTitle}</p>}
            {detailLoading && <p className="rl-cd-empty">Loading conversation detail...</p>}
            {transcript.map((item) => (
              <MessageRow
                key={item.id}
                item={item}
                approval={item.kind === 'message' ? approvals[item.id] : undefined}
                approvalBusy={item.kind === 'message' && approvalBusyId === item.id}
                onDecision={handleDraftDecision}
                onUsePrompt={setMessage}
              />
            ))}
            {sending && (
              <div className="rl-msg agent">
                <div className="m-av">
                  <SparkIcon />
                </div>
                <div className="m-body">
                  <div className="m-text">
                    <p>Generating grounded response...</p>
                  </div>
                </div>
              </div>
            )}
            {chatError && (
              <div className="rl-refuse tier2" style={{ marginTop: 18 }}>
                <div className="ic">
                  <ShieldIcon />
                </div>
                <div className="rt">
                  <b>Chat endpoint unavailable</b>
                  <br />
                  {chatError}
                </div>
              </div>
            )}
            {statusMessage && (
              <div className="rl-cd-note" role="status">
                <ArchiveIcon />
                <span>{statusMessage}</span>
              </div>
            )}
          </div>
        </div>

        <ConversationDrawer
          conversations={conversations}
          selectedId={conversationId}
          loading={conversationsLoading}
          error={conversationError}
          busyConversationId={busyConversationId}
          onNew={handleNewConversation}
          onSelect={handleSelectConversation}
          onArchiveToggle={handleArchiveToggle}
        />
      </div>

      <div className="rl-chat-composer">
        <div className="rl-composer-in">
          {!enabled && (
            <div className="rl-composer-note">
              Disabled by VITE_LINEAGE_ENABLE_COPILOT_CHAT. No chat requests are sent.
            </div>
          )}
          {activeArchived && (
            <div className="rl-composer-note">
              Archived conversation is read-only. Restore it before sending another message.
            </div>
          )}
          <div className="rl-composer-box">
            <label
              style={{
                position: 'absolute',
                width: 1,
                height: 1,
                overflow: 'hidden',
                clip: 'rect(0 0 0 0)',
              }}
              htmlFor="regenloop-copilot-question"
            >
              Ask a grounded question
            </label>
            <textarea
              id="regenloop-copilot-question"
              rows={2}
              value={message}
              disabled={!enabled || activeArchived}
              onChange={(event) => setMessage(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  void handleSend();
                }
              }}
              placeholder={
                enabled
                  ? 'Ask about lots, signals, outcomes, audit context, or draft review text...'
                  : 'Disabled by VITE_LINEAGE_ENABLE_COPILOT_CHAT'
              }
            />
            <button
              className="rl-send"
              type="button"
              aria-label="Send to co-pilot"
              disabled={!canSend}
              onClick={() => void handleSend()}
            >
              <SparkIcon />
            </button>
          </div>
          <div className="rl-composer-note">
            <ShieldIcon />{' '}
            <span>
              <b>Human-confirmed AI:</b> no autonomous release, diagnosis, medical advice, or
              efficacy claim.
            </span>
          </div>
        </div>
      </div>

    </div>
  );
}
